import { Component, OnInit } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';

import { DataExchangeService } from 'src/app/shared/services/data-exchange.service';
import { CDSService } from 'src/app/shared/services/cds.service';
import { ToolConfig } from 'src/app/shared/types/ToolConfig';
import { Season } from 'src/app/shared/types/Season';

import * as GLOBAL from 'src/app/shared/globals';
import * as moment from 'moment';
import { NgxgRequest } from 'src/app/core/comm/ngxg-request';
import { Router, NavigationStart } from '@angular/router';
import { takeUntil, filter, startWith, catchError } from 'rxjs/operators';
import { TabLoadingService } from '../tabs/utils/tab-loading.service';
import { TourService } from 'ngx-tour-md-menu';
import { MatDialog } from '@angular/material';
import { DisclaimerComponent } from '../disclaimer/disclaimer.component';
import { TutorialComponent } from '../tutorial/tutorial.component';
import { MapDirective } from 'src/app/shared/modules/map/directives/map.directive';

import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';

interface Map {
    display?: boolean;
    instance?: L.Map;
    geojson?: any;
    synced?: boolean;
}

@Component({
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent extends NgxgRequest implements OnInit {

    private isExtraSmall: Observable<BreakpointState> = this.breakpointObserver.observe(Breakpoints.XSmall);

    public statesFilter = [
        { geoid: 'US', desc: 'All States' }
    ];

    public selectedStateFilter: any = this.statesFilter[0];

    public selectedSeasonMarker = {
        latitude: null,
        longitude: null,
        divIconOptions: {
            className: '',
            iconAnchor: [0, 0],
            html: '<img width="42px" height="42px" src="assets/img/mapIcons/selected.png" />'
        },
        refitMap: true
    };

    public mapsOptions: any = {
        latitude: 21.588769,
        longitude: -78.324564,
        zoom: 10,
        controls: { baseMaps: true, locate: false, zoom: true, scrollZoom: true, clickZoom: true, fullScreen: false },
        basemap: 'Hybrid',
        fitMapTo: 'marker',
        disableMarkerClusteringAtZoom: 1
    };

    public seasons: Array<Season> = [];

    // Load last selected station/season
    public sID = localStorage.getItem('selSeason') || '59c8768d8455bc1dbde3a99e';

    // Map instances acquired on HTML trigger
    public maps: any = {
        stationsMap: {} as Map
    };

    public toolConfig: ToolConfig;
    public tabsLoading: Boolean;
    public seasonsLoading: Boolean = true;

    constructor(
        private router: Router,
        private cdsService: CDSService,
        private dataExchangeService: DataExchangeService,
        private tabLoadingService: TabLoadingService,
        private tourService: TourService,
        private dialog: MatDialog,
        private breakpointObserver: BreakpointObserver
    ) {

        super();

        if (localStorage.getItem('selState') == null) {
            this.statesFilter[0].desc = 'Select your State';
        }

        this.toolConfig = {
            season: null,
            date: {
                from: moment().subtract(30, 'days'),
                to: moment().startOf('day')
            },
            state: 'all'
        };

        this.tourService.initialize([{
            anchorId: 'ngxOne',
            // tslint:disable-next-line:max-line-length
            content: 'Use the map to navigate through the stations. Click on the weather station you want to see its information on the right panel.',
            title: 'Weather Stations Map',
        }, {
            anchorId: 'ngxTwo',
            content: 'This section displays the selected weather station information.',
            title: 'Station Info',
        }, {
            anchorId: 'ngxThree',
            content: 'Through this tab you can visualize the disease risk data for Anthracnose and Botrytis.',
            title: 'Disease Risk'
        }, {
            anchorId: 'ngxFour',
            content: 'The weather tab shows the weather station data with the Infection Indexes.',
            title: 'Weather'
        }, {
            anchorId: 'ngxFifth',
            content: 'Select the condition that best match with your field to receive a spray recommendation.',
            title: 'Recommendations'
        }, {
            anchorId: 'ngxSixth',
            // tslint:disable-next-line:max-line-length
            content: 'You can change the date period and export the data through the options available on the Disease Risk and Weather tabs.',
            title: 'Actions',
            route: 'dashboard/disease'
        }], {
            enableBackdrop: true,
            preventScrolling: true
        });

        // this.tourService.start();
    }

    openDisclaimer() {
        const dialogRef = this.dialog.open(DisclaimerComponent, { disableClose: true });

        const smallDialogSubscription = this.isExtraSmall.subscribe(result => {
            if (result.matches) {
                dialogRef.updateSize('98%', '98%');
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            smallDialogSubscription.unsubscribe();
        });

        dialogRef.beforeClosed().subscribe(result => {
            if (result === true) {
                localStorage.setItem('disclaimer', 'accept');
                if (localStorage.getItem('tour') == null || localStorage.getItem('tour') !== 'done') {
                    this.openTutorial();
                }
            }
        });
    }

    openTutorial() {
        const dialogRef = this.dialog.open(TutorialComponent, { disableClose: true, autoFocus: false });

        dialogRef.beforeClosed().subscribe(result => {
            if (result.begin === true) {
                this.tourService.start();
            }
            if (result.tourDone === true) {
                localStorage.setItem('tour', 'done');
            }

            sessionStorage.setItem('tour', 'done');
        });
    }

    ngOnInit() {

        setTimeout(() => {
            if (localStorage.getItem('disclaimer') == null || localStorage.getItem('disclaimer') !== 'accept') {
                this.openDisclaimer();
                // tslint:disable-next-line:max-line-length
            } else if (localStorage.getItem('tour') !== 'done' && sessionStorage.getItem('tour') !== 'done') {
                this.openTutorial();
            }
        });

        /**
         * Monitor tabs loading
         */

        this.router.events
            .pipe(
                filter(event => event instanceof NavigationStart),
                takeUntil(this.ngxgUnsubscribe)
            )
            .subscribe(event => {
                if (event instanceof NavigationStart) {
                    this.tabsLoading = true;
                    this.tabLoadingService.setLoading(this.tabsLoading);
                }
            });

        this.tabLoadingService.getLoading().pipe(
            startWith(true),
            takeUntil(this.ngxgUnsubscribe)
        ).subscribe(
            loading => this.tabsLoading = loading
        );

        this.loadSeasons();

    }

    private loadSeasons(): void {

        this.cdsService.getPublicSeasons('US', GLOBAL.commodityID).subscribe(result => {

            result.data.map(season => {
                const s: Season = {
                    id: season._id,
                    name: season.name,
                    weatherStation: {
                        id: season.field.weatherStations[0],
                        name: season.name,
                        latitude: season.field.location.lat,
                        longitude: season.field.location.lon,

                        location: season.field.location.location,

                        llMarker: {
                            options: {
                                latitude: season.field.location.lat,
                                longitude: season.field.location.lon,
                                divIconOptions: {
                                    className: '',
                                    iconAnchor: [0, 0],
                                    html: '<img width="42px" height="42px" src="assets/img/mapIcons/loading.png" />'
                                },
                                meta: {
                                    seasonID: season._id,
                                    enable: true
                                },
                                refitMap: true
                            }
                        }
                    },

                };

                // Load State filter select
                if (this.statesFilter.findIndex(sf => sf.geoid === s.weatherStation.location._id.substr(0, 4)) === -1) {
                    this.statesFilter.push({
                        geoid: s.weatherStation.location._id.substr(0, 4),
                        desc: s.weatherStation.location.state.name
                    });
                }

                this.seasons.push(s);
                this.loadDiseaseStatus(s);

            });

        });

    }

    private initTool(season): void {
        this.mapsOptions.fitMapTo = 'marker';

        // Load last selected state
        if (localStorage.getItem('selState') != null) {
            this.statesFilter.forEach(sf => {
                if (sf.geoid == JSON.parse(localStorage.getItem('selState')).geoid) {
                    this.selectedStateFilter = sf;
                }
            });
        }

        this.selectedSeasonMarker.divIconOptions.html = '<img width="42px" height="42px" src="assets/img/mapIcons/' + season.anthracnose.label + '_' + season.botrytis.label + '_selected.png" />'
        this.selectedSeasonMarker.latitude = season.weatherStation.latitude;
        this.selectedSeasonMarker.longitude = season.weatherStation.longitude;
        this.selectedSeasonMarker.refitMap = true;

        this.toolConfig.season = season
        this.dataExchangeService.setConfig(this.toolConfig);


    }

    private loadDiseaseStatus(season: Season): void {

        const todayDate = moment().format('YYYY-MM-DD');
        const yesterdayDate = moment().subtract(1, 'day').format('YYYY-MM-DD');

        forkJoin(
            this.cdsService.getCurrentDiseaseStatus(season.id, GLOBAL.anthracnoseID, yesterdayDate, todayDate),
            this.cdsService.getCurrentDiseaseStatus(season.id, GLOBAL.botrytisID, yesterdayDate, todayDate)
        ).pipe(catchError(e => {
            if (e.status === 404) {

                season.weatherStation.llMarker = {
                    options: {
                        latitude: season.weatherStation.latitude,
                        longitude: season.weatherStation.longitude,
                        divIconOptions: {
                            className: '',
                            iconAnchor: [0, 0],
                            html: '<img width="42px" height="42px" src="' + 'assets/img/mapIcons/loading.png' + '" />'
                        },

                        meta: {
                            seasonID: season.id,
                            enable: true
                        },
                        refitMap: true

                    }
                };

            }

            throw e;
        })).subscribe(([a, b]) => {

            season.anthracnose = {
                iIndex: a.data.diseaseSimulation[a.data.diseaseSimulation.length - 1].iIndex,
                label: a.data.diseaseSimulation[a.data.diseaseSimulation.length - 1].risk,
                dtme: a.data.diseaseSimulation[a.data.diseaseSimulation.length - 1].data.dtme
            };

            // console.log(a, b);

            season.botrytis = {
                iIndex: b.data.diseaseSimulation[b.data.diseaseSimulation.length - 1].iIndex,
                label: b.data.diseaseSimulation[b.data.diseaseSimulation.length - 1].risk,
                dtme: b.data.diseaseSimulation[b.data.diseaseSimulation.length - 1].data.dtme
            };

            season.weatherStation.llMarker = {
                options: {
                    latitude: season.weatherStation.latitude,
                    longitude: season.weatherStation.longitude,
                    divIconOptions: {
                        className: '',
                        iconAnchor: [0, 0],
                        html: '<img width="42px" height="42px" src="' + 'assets/img/mapIcons/' +
                            season.anthracnose.label + '_' + season.botrytis.label + '.png' + '" />'
                    },

                    meta: {
                        seasonID: season.id,
                        enable: true
                    },
                    refitMap: true

                }
            };

            if (season.id == this.sID) {
                this.initTool(season);
            }

        });

    }

    public markerClicked(event) {

        this.selectedSeasonMarker.latitude = null;
        this.selectedSeasonMarker.longitude = null;

        this.tabsLoading = true;
        this.tabLoadingService.setLoading(this.tabsLoading);

        // Record last selected station/season
        localStorage.setItem('selSeason', event.meta.seasonID);

        this.toolConfig.season = this.seasons.find(s => s.id === event.meta.seasonID);
        this.dataExchangeService.setConfig(this.toolConfig);

        setTimeout(() => {
            this.selectedSeasonMarker.divIconOptions.html = '<img width="42px" height="42px" src="assets/img/mapIcons/' + this.toolConfig.season.anthracnose.label + '_' + this.toolConfig.season.botrytis.label + '_selected.png" />'
            this.selectedSeasonMarker.latitude = event.latitude;
            this.selectedSeasonMarker.longitude = event.longitude;
            this.selectedSeasonMarker.refitMap = false;
        });

    }

    // Hack to scroll back to top when page changes
    public onActivate(event) {
        window.scroll(0, 0);
    }

    public mapInstance(mapID: string, llmap: MapDirective): void {
        this.maps[mapID].instance = llmap.map;

        (this.maps[mapID].instance as L.Map).setView(
            [this.mapsOptions.latitude, this.mapsOptions.longitude],
            this.mapsOptions.zoom,
            null
        );
    }

    public selectedStateChange(event) {
        // Record last selected state
        localStorage.setItem('selState', JSON.stringify(event));

        this.selectedSeasonMarker.latitude = null;
        this.selectedSeasonMarker.longitude = null;

        setTimeout(() => {
            if (this.toolConfig.season.weatherStation.location.state.name == event.desc) {
                this.selectedSeasonMarker.divIconOptions.html = '<img width="42px" height="42px" src="assets/img/mapIcons/' + this.toolConfig.season.anthracnose.label + '_' + this.toolConfig.season.botrytis.label + '_selected.png" />'

                this.selectedSeasonMarker.latitude = this.toolConfig.season.weatherStation.latitude;
                this.selectedSeasonMarker.longitude = this.toolConfig.season.weatherStation.longitude;
            }
        }, 200);

    }

}
